import axios from 'axios'
import {QueryFunction, useQuery} from 'react-query'
import {useSelector} from 'react-redux'

import {AppState} from '../../../../../Root.store'
import {getUserProfileCountry} from '../../../../../UserProfile/UserProfile.selectors'
import {RegionFeatures} from '../../../orderIntake/config/regionFeatures'

import defaultRemoteFeatures from './defaultCountryFeatures.json'

// Production is served from the unsuffixed host, every other stage from `static-<stage>`.
const STATIC_BASE_URL =
  process.env.REACT_APP_STAGE === 'prod'
    ? 'https://static.hcag.app'
    : `https://static-${process.env.REACT_APP_STAGE}.hcag.app`

/**
 * Type alias for backward compatibility
 */
export type OrderProcessCountryFeatures = RegionFeatures

export type RemoteOrderProcessCountryFeatures = {
  alwaysHasTimeOnJobSite: string[]
  hasPumpSelector: string[]
  hasTruckTypeSelector: string[]
  hasUnloadingMethod: string[]
  canConfirmOrder: string[]
  hasConcreteUsage: string[]
  hasAdmixtures: string[]
  allowTimeOnJobSite: string[]
  allowFlowRate: string[]
  hasDriverInstructions: string[]
  hasTesting: string[]
  hasNotes: string[]
}

const getDefaultFeatures = (
  remoteFeatures: RemoteOrderProcessCountryFeatures,
  country: string
): OrderProcessCountryFeatures => {
  return Object.keys(remoteFeatures).reduce((prev, current) => {
    const key = current as keyof RemoteOrderProcessCountryFeatures
    prev[key] = remoteFeatures[key].includes(country)
    return prev
  }, {} as OrderProcessCountryFeatures)
}

/**
 * Hook to get country-specific features for RMC order intake
 */
export const useRmcCountryFeatures = (): OrderProcessCountryFeatures => {
  const api = axios.create({
    baseURL: STATIC_BASE_URL
  })
  const country = useSelector((state: AppState) => getUserProfileCountry(state)) || 'DE'

  const fetcher: QueryFunction<
    Partial<OrderProcessCountryFeatures>,
    [string, {country: string}]
  > = async ({queryKey}) => {
    try {
      const [, {country}] = queryKey
      const config = await api.get('/onsite/config/orderIntake.json')
      const featuresByCountry = config.data as RemoteOrderProcessCountryFeatures
      return getDefaultFeatures(featuresByCountry, country)
    } catch (error) {
      console.error('Error fetching remote country features:', error)
      // Return empty object to fallback to default features
      return {}
    }
  }

  const queryInfo = useQuery(['orderProcessFeaturesByCountry', {country}], fetcher, {
    cacheTime: 90 * 1000,
    staleTime: 90 * 1000,
    retry: 1
  })

  // Return combination of default features and remote features
  return {
    ...getDefaultFeatures(defaultRemoteFeatures as RemoteOrderProcessCountryFeatures, country),
    ...queryInfo.data
  }
}
